// ui/pages/VisitaFormPage.jsx  (modo edición si hay :uuid en la ruta)
// Página = carga (useVisitaForm) + cuerpo. El cuerpo se monta con la entidad ya cargada, así el editor de
// imágenes arranca con sus valores iniciales sin efectos de sincronización.
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import BlockUi from 'react-block-ui';
import { useAuth } from '../../../../../components/content/auth.jsx';
import BackButton from '../../../../../components/link/back-button.jsx';
import { useVisitaForm } from '../../application/useVisitaForm';
import { useVisitaCatalogos } from '../../application/useVisitaCatalogos';
import { useImagenesEditor } from '../../../shared/application/useImagenesEditor';
import { showAviso, showError, showSuccess } from '../../../shared/ui/feedback';
import { mensajeResultadoParcial, mensajesAgregarImagenes, soloExcedeLimite, MSG_ESPERAR_OPTIMIZACION } from '../../../shared/domain/media';
import { conCicloPorDefecto } from '../../../shared/domain/ciclo';
import ImagenesSection from '../../../shared/ui/ImagenesSection.jsx';
import { guardarPendientesParaEdicion, tomarPendientes } from '../../../shared/ui/imagenesHandoff';
import { idProductor } from '../../domain/visita';
import VisitaForm from '../components/VisitaForm.jsx';
import { VISITAS_PATH } from '../../../constants';

const listPath = `${VISITAS_PATH}/list`;

const VisitaFormBody = ({ data, isEdit, saving, save, reintentarImagen }) => {
  const history = useHistory();
  const { user } = useAuth();

  // Productor elegido por el usuario; mientras no cambie se usa el de la visita cargada (estado derivado).
  const [productorElegido, setProductorElegido] = React.useState(undefined);
  const idCuenta = productorElegido !== undefined ? productorElegido : idProductor(data);

  // Al crear se propone el ciclo activo (no hay campo en la UI); el formulario se monta hasta tener los ciclos.
  const catalogos = useVisitaCatalogos({
    idCooperativa: user ? user.id_cooperativa : undefined,
    idCiclo: data.id_ciclo,
    idCuenta,
  });
  const base = React.useMemo(() => (isEdit ? data : conCicloPorDefecto(data, catalogos.ciclos)), [isEdit, data, catalogos.ciclos]);
  const initialValues = React.useMemo(() => ({ ...base, id_cuenta: idProductor(data) }), [base, data]);
  const formReady = isEdit || catalogos.ciclosReady;

  return formReady ? (
    <BlockUi tag="div" message="" blocking={catalogos.loading} renderChildren={true}>
      <FormWithEditors
        data={data}
        initialValues={initialValues}
        catalogos={catalogos}
        isEdit={isEdit}
        saving={saving}
        save={save}
        reintentarImagen={reintentarImagen}
        onProductorChange={setProductorElegido}
        history={history}
      />
    </BlockUi>
  ) : null;
};

const FormWithEditors = ({ data, initialValues, catalogos, isEdit, saving, save, reintentarImagen, onProductorChange, history }) => {
  // Imágenes que fallaron (o no llegaron a subirse) antes de pasar de crear a editar: ver shared/ui/imagenesHandoff.js.
  const [pendientesIniciales] = React.useState(() => tomarPendientes(data.uuid));
  const imagenes = useImagenesEditor({ saved: data.media, pendientes: pendientesIniciales });

  const handleRetry = item => reintentarImagen(item, imagenes.marcar);

  const handleFiles = async files => {
    const resultado = await imagenes.addFiles(files);
    const mensajes = mensajesAgregarImagenes(resultado);
    if (mensajes.length) (soloExcedeLimite(resultado) ? showAviso : showError)({ messages: mensajes });
  };

  const handleSubmit = async (values, setFormError) => {
    if (imagenes.procesando) {
      showAviso({ messages: [MSG_ESPERAR_OPTIMIZACION] });
      return;
    }
    const pendientes = imagenes.porSubir;
    const editPath = guardado => `${VISITAS_PATH}/edit/${guardado.uuid}`;
    try {
      const { guardado, imagenes: resultado, eliminaciones } = await save(values, {
        pendientes,
        eliminaciones: imagenes.paraEliminar,
        onImagenEstado: imagenes.marcar,
        onEliminacionEstado: imagenes.marcarEliminacion,
      });
      if (resultado.fallidas.length || eliminaciones.fallidas.length) {
        // Datos guardados, imágenes no: se queda en la página. Al crear se pasa a editar (para no duplicar) llevando las fallidas.
        if (!isEdit) {
          const motivos = new Map(resultado.fallidas.map(f => [f.uuid, f.error]));
          guardarPendientesParaEdicion(
            guardado.uuid,
            pendientes.filter(p => motivos.has(p.uuid)).map(p => ({ ...p, estado: 'error', error: motivos.get(p.uuid) }))
          );
          history.replace(editPath(guardado));
        }
        await showError({
          message: mensajeResultadoParcial('La visita', { eliminar: eliminaciones.fallidas.length, subir: resultado.fallidas.length }),
        });
        return;
      }
      showSuccess(isEdit ? 'Visita actualizada' : 'Visita creada');
      history.push(listPath);
    } catch (e) {
      if (e && e.fieldErrors) {
        Object.keys(e.fieldErrors).forEach(field => {
          setFormError(field, { type: 'server', message: [].concat(e.fieldErrors[field]).join(' ') });
        });
      }
      // El padre ya existe (falló un paso posterior a guardarlo): al crear se va a su edición para reintentar sin duplicar,
      // llevando las imágenes aún pendientes (no se subió ninguna).
      if (e && e.guardado && !isEdit) {
        guardarPendientesParaEdicion(e.guardado.uuid, pendientes);
        history.replace(editPath(e.guardado));
      }
      await showError(e);
    }
  };

  return (
    <VisitaForm
      initialValues={initialValues}
      catalogos={catalogos}
      submitting={saving}
      onSubmit={handleSubmit}
      onCancel={() => history.push(listPath)}
      onProductorChange={onProductorChange}>
      <ImagenesSection
        saved={imagenes.saved}
        pending={imagenes.data}
        total={imagenes.total}
        limiteAlcanzado={imagenes.limiteAlcanzado}
        subtitle="Evidencia fotográfica de la visita."
        disabled={saving}
        onFiles={handleFiles}
        entidad="visita"
        onRemove={imagenes.remove}
        onRetry={handleRetry}
        onPendingDone={imagenes.confirmarSubida}
        onMarkDelete={imagenes.marcarParaEliminar}
        onUndoDelete={imagenes.deshacerEliminar}
      />
    </VisitaForm>
  );
};

const VisitaFormPage = () => {
  const { uuid } = useParams();
  const { data, status, error, isEdit, saving, save, reintentarImagen } = useVisitaForm({ uuid });

  React.useEffect(() => {
    if (status === 'error') showError(error);
  }, [status, error]);

  return (
    <>
      <div className="mb-3">
        <BackButton label="Volver" goBackDefault={listPath} disabled={saving} />
      </div>
      <h1 className="page-header">{isEdit ? 'Editar Visita' : 'Registrar Visita'}</h1>
      <BlockUi tag="div" message="" blocking={status === 'loading'} renderChildren={true}>
        {data && <VisitaFormBody data={data} isEdit={isEdit} saving={saving} save={save} reintentarImagen={reintentarImagen} />}
      </BlockUi>
    </>
  );
};

export default VisitaFormPage;
